// "Remember my password on this device" — independent of the browser's own
// password manager (Chrome refuses to save passwords on pages whose HTTPS
// certificate it does not trust, which is the case for this site).
//
// The password is encrypted with an AES-GCM key that is generated once and kept
// in IndexedDB as a NON-EXTRACTABLE CryptoKey, so the stored blob is not
// readable from a copy of the site data and the key itself cannot be exported.
// It does not defend against someone using the unlocked phone's browser, which
// is why the login screen makes this opt-in per device.
const DB_NAME = "tulsi_login";
const STORE = "kv";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const r = db.transaction(STORE).objectStore(STORE).get(key);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

async function idbSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbDelete(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getKey() {
  let key = await idbGet("key");
  if (!key) {
    key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await idbSet("key", key);
  }
  return key;
}

export function savedLoginSupported() {
  return !!(window.indexedDB && window.crypto?.subtle);
}

export async function saveLogin({ type, identifier, password }) {
  if (!savedLoginSupported()) return false;
  try {
    const key = await getKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = new TextEncoder().encode(JSON.stringify({ type, identifier, password }));
    const blob = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
    await idbSet("cred", { iv, blob });
    return true;
  } catch { return false; }
}

export async function loadLogin() {
  if (!savedLoginSupported()) return null;
  try {
    const rec = await idbGet("cred");
    if (!rec) return null;
    const key = await idbGet("key");
    if (!key) return null;
    const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: rec.iv }, key, rec.blob);
    return JSON.parse(new TextDecoder().decode(plain));
  } catch { return null; }
}

export async function clearSavedLogin() {
  try { await idbDelete("cred"); } catch { /* nothing saved */ }
}
